import { ColumnDef, Row } from "@tanstack/table-core/build/lib/types"
import { BadgeDollarSign, CircleDashed } from "lucide-react"
import { TableTransactionCurrency } from "../data/types"
import { TableOrder } from "../data/types"
import { TableMovement } from "../data/types"
import { TableData } from "../../../ui/data-table/data-table-toolbar"

export const tableOrderCurrencies = [
	"USDC",
	"USDT",
	"DAI",
	"BNB",
	"BUSD",
	"ARS",
	"BRL",
]

export function CurrencyImageCell<T extends TableData>(
	currencyAccessorKey?: string
): ColumnDef<T> {
	return {
		accessorKey: "currency",
		header: () => (
			<div className="text-center justify-center text-h6 items-center flex flex-row gap-2">
				<BadgeDollarSign className="size-icon" />
				Moneda
			</div>
		),
		cell: ({ row }: { row: Row<T> }) => {
			const currency = row.getValue("currency") as
				| "USDC"
				| "USDT"
				| "DAI"
				| "BNB"
				| "BUSD"
				| "ARS"
				| "BRL"
			return <CurrencyImage currency={currency} />
		},
		filterFn: (row, id, value) => {
			return value.includes(row.getValue(id))
		},
	}
}

export const CurrencyImage = ({
	currency,
}: {
	currency: TableTransactionCurrency
}) => {
	return (
		<div className="flex justify-center items-center">
			<div className="justify-center items-center w-7 h-7">
				{getCurrencyImage(currency)}
			</div>
		</div>
	)
}

export function getCurrencyImage(currency: TableTransactionCurrency) {
	switch (currency) {
		case "USDC":
			return usdcIcon
		case "USDT":
			return tetherIcon
		case "DAI":
			return daiIcon
		case "BNB":
			return bnbIcon
		case "BUSD":
			return busdIcon
		case "ARS":
			return arsIcon
		case "BRL":
			return brazilIcon
		case undefined:
			return undefinedCurrencyIcon
		default:
			return undefinedCurrencyIcon
	}
}

const undefinedCurrencyIcon = <CircleDashed size={24} />

const arsIcon = (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" fill="none">
		<mask
			id="mask0_22750_1621"
			mask="mask-type:luminance"
			maskUnits="userSpaceOnUse"
			x="0"
			y="0"
			width="28"
			height="28"
		>
			<path
				d="M14 28C21.732 28 28 21.732 28 14C28 6.26801 21.732 0 14 0C6.26801 0 0 6.26801 0 14C0 21.732 6.26801 28 14 28Z"
				fill="white"
			/>
		</mask>
		<g mask="url(#mask0_22750_1621)">
			<path
				d="M0 0H28V7.91328L26.6875 14L28 20.0867V28H0V20.0867L1.42188 14L0 7.91328V0Z"
				fill="#338AF3"
			/>
			<path d="M0 7.91333H28V20.0868H0V7.91333Z" fill="white" />
			<path
				d="M18.1781 14L16.4719 14.804L17.3852 16.461L15.5258 16.1055L15.2961 17.9813L14 16.6032L12.7094 17.9813L12.4742 16.1055L10.6148 16.461L11.5227 14.804L9.81641 14L11.5281 13.1961L10.6094 11.5391L12.4688 11.8946L12.7039 10.0188L13.9945 11.3969L15.2852 10.0188L15.5258 11.8946L17.3852 11.5391L16.4719 13.1961L18.1781 14Z"
				fill="#FFDA44"
			/>
		</g>
	</svg>
)

const brazilIcon = (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" fill="none">
		<mask
			id="mask0_22750_1630"
			mask="mask-type:luminance"
			maskUnits="userSpaceOnUse"
			x="0"
			y="0"
			width="28"
			height="28"
		>
			<path
				d="M14 28C21.732 28 28 21.732 28 14C28 6.26801 21.732 0 14 0C6.26801 0 0 6.26801 0 14C0 21.732 6.26801 28 14 28Z"
				fill="white"
			/>
		</mask>
		<g mask="url(#mask0_22750_1630)">
			<path d="M0 0H28V28H0V0Z" fill="#6DA544" />
			<path
				d="M14 5.47974L25.5664 14L14 22.5204L2.43359 14L14 5.47974Z"
				fill="#FFDA44"
			/>
			<path
				d="M9.5264 12.086C9.2543 12.7118 9.12002 13.3889 9.13265 14.0712L17.992 16.7946C18.3823 16.2371 18.6507 15.6035 18.7795 14.9352C16.5592 11.3641 12.2334 10.5438 9.53187 12.0915L9.5264 12.086Z"
				fill="#EEEEEE"
			/>
			<path
				d="M13.9836 9.13283C13.1838 9.13485 12.3968 9.33395 11.6922 9.71252C10.7225 10.2357 9.95897 11.0725 9.52656 12.086C11.1835 11.7341 12.9029 11.8117 14.5214 12.3113C16.14 12.811 17.6039 13.716 18.7742 14.9406C18.9916 13.8335 18.8176 12.6852 18.282 11.6922C17.8643 10.9165 17.2438 10.2687 16.4868 9.81799C15.7298 9.36726 14.8646 9.13045 13.9836 9.13283ZM11.5937 13.6992C10.7592 13.6964 9.92924 13.8219 9.13281 14.0711C9.14202 14.852 9.33897 15.6191 9.70703 16.3078C10.0099 16.8711 10.4208 17.3691 10.9162 17.7734C11.4117 18.1778 11.9819 18.4805 12.5944 18.6644C13.2069 18.8482 13.8497 18.9096 14.4859 18.8449C15.1221 18.7803 15.7394 18.5908 16.3023 18.2875C16.9708 17.9276 17.5457 17.4163 17.9812 16.7945C17.2137 15.8362 16.242 15.061 15.137 14.5256C14.0321 13.9901 12.8216 13.7078 11.5937 13.6992Z"
				fill="#0052B4"
			/>
		</g>
	</svg>
)

const busdIcon = (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30" fill="none">
		<path
			d="M15.0072 1.72266L18.2968 5.08148L10.0133 13.3402L6.72363 10.0604L15.0072 1.72266Z"
			fill="#F0B90B"
			stroke="#F0B90B"
			stroke-width="1.25"
		/>
		<path
			d="M20.0011 6.70166L23.2907 10.0605L10.0133 23.2982L6.72363 20.0184L20.0011 6.70166Z"
			fill="#F0B90B"
			stroke="#F0B90B"
			stroke-width="1.25"
		/>
		<path
			d="M5.01962 11.6807L8.30925 15.0395L5.01962 18.3193L1.72998 15.0395L5.01962 11.6807Z"
			fill="#F0B90B"
			stroke="#F0B90B"
			stroke-width="1.25"
		/>
		<path
			d="M24.9952 11.6807L28.2848 15.0395L15.0074 28.2772L11.7178 24.9974L24.9952 11.6807Z"
			fill="#F0B90B"
			stroke="#F0B90B"
			stroke-width="1.25"
		/>
	</svg>
)

const tetherIcon = (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
		<path
			d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z"
			fill="#26A17B"
		/>
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M13.4416 13.0373V13.0358C13.3591 13.0418 12.9338 13.0673 11.9851 13.0673C11.2276 13.0673 10.6943 13.0448 10.5068 13.0358V13.038C7.59085 12.9098 5.41435 12.402 5.41435 11.7945C5.41435 11.1878 7.59085 10.68 10.5068 10.5495V12.5325C10.6973 12.546 11.2433 12.5783 11.9978 12.5783C12.9031 12.5783 13.3568 12.5408 13.4416 12.5333V10.551C16.3516 10.6808 18.5228 11.1885 18.5228 11.7945C18.5228 12.402 16.3516 12.9083 13.4416 13.0373V13.0373ZM13.4416 10.3448V8.57026H17.5021V5.86426H6.44635V8.57026H10.5068V10.344C7.20685 10.4955 4.7251 11.1495 4.7251 11.9325C4.7251 12.7155 7.20685 13.3688 10.5068 13.521V19.2075H13.4416V13.5195C16.7363 13.368 19.2121 12.7148 19.2121 11.9325C19.2121 11.1503 16.7363 10.497 13.4416 10.3448"
			fill="white"
		/>
	</svg>
)

const daiIcon = (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" fill="none">
		<g clip-path="url(#clip0_22751_1696)">
			<path
				d="M14 28C21.7324 28 28 21.7324 28 14C28 6.26762 21.7324 0 14 0C6.26762 0 0 6.26762 0 14C0 21.7324 6.26762 28 14 28Z"
				fill="url(#paint0_linear_22751_1696)"
			/>
			<path
				d="M22.3277 11.7153H20.6644C20.1976 10.4486 19.3536 9.35541 18.2461 8.58327C17.1387 7.81114 15.8211 7.39718 14.4711 7.39722H8.69698V11.7153H6.86035V13.2273H8.69785V14.8461H6.86035V16.3572H8.69785V20.6027H14.4711C15.8078 20.6028 17.1131 20.197 18.214 19.4388C19.315 18.6807 20.1597 17.6061 20.6364 16.3572H22.3277V14.8452H21.017C21.0511 14.5713 21.073 14.2852 21.073 14C21.073 13.7375 21.0564 13.4793 21.0284 13.2273H22.333V11.7153H22.3277ZM10.209 8.84272H14.409C15.3616 8.84278 16.2948 9.11261 17.1005 9.62098C17.9062 10.1294 18.5515 10.8555 18.9616 11.7153H10.209V8.84272ZM14.409 19.1572H10.209V16.3572H18.9284C18.5106 17.1985 17.8661 17.9065 17.0676 18.4012C16.2691 18.8959 15.3483 19.1578 14.409 19.1572ZM19.4551 13.888V14.1172C19.4551 14.364 19.4376 14.6107 19.4044 14.8452H10.209V13.2265H19.4096C19.4376 13.4452 19.4551 13.664 19.4551 13.888Z"
				fill="white"
			/>
		</g>
		<defs>
			<linearGradient
				id="paint0_linear_22751_1696"
				x1="4.10025"
				y1="4.10025"
				x2="23.8997"
				y2="23.8997"
				gradientUnits="userSpaceOnUse"
			>
				<stop stop-color="#F9AF1A" />
				<stop offset="1" stop-color="#FBC349" />
			</linearGradient>
			<clipPath id="clip0_22751_1696">
				<rect width="28" height="28" fill="white" />
			</clipPath>
		</defs>
	</svg>
)

const usdcIcon = (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" fill="none">
		<g clip-path="url(#clip0_22751_1695)">
			<path
				d="M14 28C21.7324 28 28 21.7324 28 14C28 6.26762 21.7324 0 14 0C6.26762 0 0 6.26762 0 14C0 21.7324 6.26762 28 14 28Z"
				fill="#2775C9"
			/>
			<path
				d="M14 24.5C8.2005 24.5 3.5 19.7995 3.5 14C3.5 8.2005 8.2005 3.5 14 3.5C19.7995 3.5 24.5 8.2005 24.5 14C24.5 16.7848 23.3938 19.4555 21.4246 21.4246C19.4555 23.3938 16.7848 24.5 14 24.5ZM13.3744 10.1036C12.7998 10.1322 12.2567 10.3745 11.8517 10.7829C11.4466 11.1914 11.2089 11.7365 11.1851 12.3112C11.1851 13.3927 11.8466 14.098 13.2501 14.3938L14.2319 14.6256C15.1882 14.8488 15.582 15.1708 15.582 15.7159C15.582 16.261 14.8925 16.7974 14 16.7974C13.6826 16.8264 13.3634 16.7655 13.079 16.6216C12.7945 16.4777 12.5564 16.2566 12.3917 15.9836C12.3429 15.8801 12.2659 15.7925 12.1694 15.7309C12.073 15.6693 11.9611 15.6362 11.8466 15.6354H11.319C11.2785 15.6429 11.2398 15.6584 11.2053 15.681C11.1707 15.7035 11.141 15.7326 11.1177 15.7667C11.0945 15.8008 11.0782 15.8391 11.0698 15.8795C11.0614 15.9198 11.061 15.9615 11.0687 16.002C11.1972 16.5262 11.4959 16.993 11.918 17.3293C12.3401 17.6656 12.8619 17.8524 13.4015 17.8605V18.6112C13.4015 18.7783 13.4679 18.9386 13.586 19.0567C13.7042 19.1749 13.8644 19.2413 14.0315 19.2413C14.1986 19.2413 14.3588 19.1749 14.477 19.0567C14.5951 18.9386 14.6615 18.7783 14.6615 18.6112V17.8518C15.2716 17.8448 15.8549 17.5999 16.2872 17.1692C16.7195 16.7386 16.9666 16.1562 16.9759 15.5461C16.9759 14.4113 16.3231 13.7585 14.7779 13.4286L13.8836 13.2317C12.9902 13.0077 12.5703 12.7129 12.5703 12.2124C12.5703 11.7119 13.1066 11.158 14 11.158C14.2815 11.1287 14.5655 11.1814 14.8177 11.31C15.0699 11.4386 15.2794 11.6374 15.421 11.8825C15.4785 12.0048 15.5694 12.1082 15.6832 12.181C15.797 12.2538 15.929 12.2929 16.0641 12.2937H16.4841C16.5797 12.2705 16.6624 12.2105 16.7143 12.1269C16.7661 12.0433 16.7831 11.9426 16.7615 11.8466C16.6406 11.3627 16.3699 10.9293 15.9881 10.6082C15.6062 10.2872 15.1328 10.095 14.6353 10.059V9.44213C14.6353 9.27504 14.5689 9.1148 14.4507 8.99665C14.3326 8.8785 14.1723 8.81213 14.0053 8.81213C13.8382 8.81213 13.6779 8.8785 13.5598 8.99665C13.4416 9.1148 13.3752 9.27504 13.3752 9.44213V10.1036H13.3744ZM6.12762 14C6.12865 15.646 6.64673 17.2501 7.60869 18.5857C8.57066 19.9214 9.92791 20.9211 11.4887 21.4436H11.6139C11.6667 21.4436 11.7191 21.4332 11.7679 21.413C11.8167 21.3928 11.8611 21.3631 11.8985 21.3257C11.9359 21.2884 11.9655 21.244 11.9857 21.1952C12.006 21.1463 12.0164 21.094 12.0164 21.0411V20.8539C12.0165 20.6875 11.9673 20.5249 11.8749 20.3866C11.7826 20.2483 11.6512 20.1405 11.4975 20.0769C10.2821 19.586 9.24106 18.7432 8.50799 17.6565C7.77493 16.5698 7.38328 15.2889 7.38328 13.9781C7.38328 12.6673 7.77493 11.3864 8.50799 10.2998C9.24106 9.2131 10.2821 8.37026 11.4975 7.87937C11.6506 7.81727 11.7818 7.71093 11.8742 7.57394C11.9666 7.43694 12.0161 7.27551 12.0164 7.11025V6.90462C12.0167 6.84438 12.0025 6.78494 11.975 6.73132C11.9476 6.67769 11.9077 6.63144 11.8586 6.59645C11.8096 6.56147 11.7528 6.53877 11.6932 6.53028C11.6336 6.52178 11.5727 6.52773 11.5159 6.54762C9.94855 7.06603 8.58439 8.06506 7.61714 9.40285C6.64989 10.7406 6.12871 12.3492 6.12762 14ZM21.8724 14C21.8697 12.3555 21.3508 10.7533 20.3889 9.41936C19.4271 8.08544 18.0708 7.0871 16.5113 6.56512H16.3774C16.266 6.56512 16.1592 6.60937 16.0804 6.68814C16.0016 6.76691 15.9574 6.87373 15.9574 6.98512V7.119C15.9607 7.29151 16.014 7.45937 16.1107 7.60224C16.2075 7.74511 16.3435 7.85687 16.5025 7.924C17.7152 8.41629 18.7535 9.25918 19.4845 10.3448C20.2154 11.4305 20.6059 12.7096 20.6059 14.0184C20.6059 15.3272 20.2154 16.6062 19.4845 17.6919C18.7535 18.7776 17.7152 19.6205 16.5025 20.1128C16.3467 20.1806 16.2135 20.2915 16.1185 20.4323C16.0235 20.5732 15.9706 20.7383 15.9661 20.9081V21.0595C15.9668 21.1258 15.9832 21.191 16.0139 21.2497C16.0446 21.3084 16.0887 21.3591 16.1428 21.3975C16.1968 21.4359 16.2591 21.461 16.3247 21.4706C16.3902 21.4803 16.4572 21.4744 16.52 21.4532C18.0808 20.9287 19.4372 19.9269 20.3977 18.5894C21.3581 17.252 21.874 15.6466 21.8724 14Z"
				fill="white"
			/>
		</g>
		<defs>
			<clipPath id="clip0_22751_1695">
				<rect width="28" height="28" fill="white" />
			</clipPath>
		</defs>
	</svg>
)

const bnbIcon = (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" fill="none">
		<g clip-path="url(#clip0_22751_1697)">
			<path
				d="M14 28C21.7324 28 28 21.7324 28 14C28 6.26762 21.7324 0 14 0C6.26762 0 0 6.26762 0 14C0 21.7324 6.26762 28 14 28Z"
				fill="#F0B90B"
			/>
			<path
				d="M14.1325 5.25L9.48535 7.93012L11.1942 8.92062L14.1342 7.23012L17.0725 8.92062L18.7814 7.93012L14.1325 5.25ZM17.0716 10.3206L18.7805 11.3111V13.2912L15.8414 14.9817V18.3619L14.1325 19.3524L12.4236 18.3619V14.9817L9.48535 13.2912V11.3111L11.1942 10.3206L14.1342 12.0111L17.0716 10.3206Z"
				fill="white"
			/>
			<path
				d="M18.7802 14.6912V16.6722L17.0713 17.6618V15.6817L18.7802 14.6912Z"
				fill="white"
			/>
			<path
				d="M17.0538 19.0619L19.9938 17.3723V13.9913L21.7017 13.0016V18.3619L17.0546 21.0429L17.0538 19.0619ZM19.9938 10.6111L18.2849 9.62063L19.9938 8.63013L21.7017 9.62063V11.6016L19.9938 12.5913V10.6111ZM12.4233 21.7595V19.7794L14.1321 20.769L15.841 19.7794V21.7595L14.1321 22.75L12.4233 21.7595ZM11.193 17.6619L9.48412 16.6723V14.6913L11.193 15.6818V17.6619ZM14.1321 10.6111L12.4233 9.62063L14.1321 8.63013L15.841 9.62063L14.1321 10.6111ZM9.98025 9.62063L8.27138 10.6111V12.5913L6.5625 11.6025V9.6215L8.27138 8.631L9.98025 9.6215V9.62063Z"
				fill="white"
			/>
			<path
				d="M6.5625 13.0017L8.27138 13.9913V17.3723L11.2105 19.062V21.043L6.5625 18.362V13.0017Z"
				fill="white"
			/>
		</g>
		<defs>
			<clipPath id="clip0_22751_1697">
				<rect width="28" height="28" fill="white" />
			</clipPath>
		</defs>
	</svg>
)
